/* eslint-disable no-underscore-dangle */
import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Pagination,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DataObjectIcon from '@mui/icons-material/DataObject'

// Matches the status vocabulary written by the check-in app (see api mutations/activity.js).
const STATUS_COLOR = {
  checkin: 'success',
  manualcheckin: 'success',
  selfcheckin: 'success',
  pickup: 'primary',
  faceMatched: 'info',
  faceUnmatched: 'warning',
  faceChecking: 'default',
}

const ActivityTableSection = ({
  activities,
  totalCount,
  loading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAllOnPage,
  onClearSelection,
  onDeleteSelectedClick,
  onDeleteRowClick,
  onViewRawClick,
  page,
  perPage,
  pageCount,
  onPageChange,
  onPerPageChange,
}) => {
  const pageIds = activities.map((a) => a._id)
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))
  const someOnPageSelected = pageIds.some((id) => selectedIds.includes(id))

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1, sm: 1.25 },
            bgcolor: 'action.selected',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            {selectedIds.length} activit{selectedIds.length !== 1 ? 'ies' : 'y'} selected
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={onDeleteSelectedClick}
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              sx={{ textTransform: 'none', fontWeight: 500 }}>
              Delete
            </Button>
            <Button
              onClick={onClearSelection}
              size="small"
              sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Clear
            </Button>
          </Stack>
        </Box>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 860 }}>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allOnPageSelected}
                  indeterminate={someOnPageSelected && !allOnPageSelected}
                  onChange={() => onToggleSelectAllOnPage(pageIds, !allOnPageSelected)}
                  inputProps={{ 'aria-label': 'Select all activities on this page' }}
                />
              </TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Bib</TableCell>
              <TableCell>Participant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Device</TableCell>
              <TableCell align="right">Confidence</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && activities.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && activities.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No activities match the current filters.
                </TableCell>
              </TableRow>
            )}
            {activities.map((row) => {
              const selected = selectedIds.includes(row._id)
              const name = row.participant
                ? `${row.participant.firstname || ''} ${row.participant.lastname || ''}`.trim()
                : ''
              return (
                <TableRow key={row._id} hover selected={selected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => onToggleSelect(row._id)}
                      inputProps={{ 'aria-label': `Select activity ${row._id}` }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss') : '-'}
                  </TableCell>
                  {/* bib is stored on the activity itself — avoids relying on the
                      `participant` relation, which is a lookup per row */}
                  <TableCell sx={{ fontWeight: 500 }}>{row.bib || '-'}</TableCell>
                  <TableCell>{name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status || 'unknown'}
                      size="small"
                      color={STATUS_COLOR[row.status] || 'default'}
                      variant={STATUS_COLOR[row.status] ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{row.deviceId || '-'}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {row.confidence != null ? row.confidence : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View raw record">
                      <IconButton size="small" onClick={() => onViewRawClick(row)}>
                        <DataObjectIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => onDeleteRowClick(row)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {totalCount} total
          </Typography>
          <Select
            size="small"
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            sx={{ minWidth: 88 }}>
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n} / page
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Pagination
          count={pageCount || 1}
          page={page}
          onChange={(e, value) => onPageChange(value)}
          size="small"
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Paper>
  )
}

ActivityTableSection.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  activities: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onToggleSelectAllOnPage: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onDeleteSelectedClick: PropTypes.func.isRequired,
  onDeleteRowClick: PropTypes.func.isRequired,
  onViewRawClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
}

export default ActivityTableSection
